import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ElementType, PropsWithChildren, ReactNode } from "react";

import { Typography } from "@/components/ui-v2";

interface PrimaryNavigationItemBaseProps extends PropsWithChildren {
  active?: boolean;
  icon: ReactNode;
  label: string;
  as?: ElementType;
  [key: string]: any; // Allow any additional props to be passed through
}

const NavigationItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>(({ theme, active = false }) => ({
  height: theme.spacing(9),
  display: "flex",
  flexGrow: 1,
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(1),
  borderRadius: theme.spacing(1),
  color: theme.palette.secondary[3],
  border: "none",
  boxShadow: "inset 0 0 0 0.5px transparent",
  textDecoration: "none",
  cursor: "pointer",
  padding: 0,
  backgroundColor: "transparent",
  width: "100%",

  "&:hover": {
    backgroundColor: theme.palette.secondary[7],
    boxShadow: `inset 0 0 0 0.5px ${theme.palette.secondary[6]}`,
  },

  ...(active && {
    color: theme.palette.secondary[1],
    backgroundColor: theme.palette.secondary[7],
    boxShadow: `inset 0 0 0 0.5px ${theme.palette.secondary[6]}`,
  }),
})) as typeof Box;

const IconContainer = styled(Box)(({ theme }) => ({
  width: theme.spacing(9),
  height: theme.spacing(9),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

const ContentContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  flex: 1,
  justifyContent: "space-between",
}));

export const PrimaryNavigationItemBase = (
  props: PrimaryNavigationItemBaseProps,
) => {
  const { active = false, icon, label, as = "div", children, ...rest } = props;

  return (
    <NavigationItem component={as} active={active} {...rest}>
      <IconContainer>{icon}</IconContainer>
      <ContentContainer>
        <Typography variant="small" fontWeight={500} color="secondary.1">
          {label}
        </Typography>
        {children}
      </ContentContainer>
    </NavigationItem>
  );
};
